import { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { AppConfigContext } from "../context/AppConfigContext";
import config from "../config";

/**
 * Custom hook for handling user authentication and Strapi user management
 * Manages authentication state, JWT tokens, and user profile data
 * 
 * @returns {Object} Authentication state and functions
 */
export const useAuth = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Authentication context - core auth state from Cognito
    const { 
        user,         // Cognito user object
        signOut,      // Function to sign out user
        accessToken,  // Cognito access token
        idToken,      // Cognito ID token
        setJwt,       // Function to set Strapi JWT
        jwt,          // Strapi JWT token
        service       // Current service type (classic/mapping)
    } = useContext(AuthContext);
    
    // Application configuration context - user limits and settings
    const {
        setMaxSpace,     // Function to set user's storage limit
        setMaxFileSize   // Function to set user's file size limit
    } = useContext(AppConfigContext);

    // Local state for extended user information
    const [userAuth, setUserAuth] = useState({});  // Strapi user profile data
    const [loading, setLoading] = useState(false); // Loading state for auth operations

    /**
     * Monitor authentication state changes and redirect if unauthenticated
     * Exempt public routes from authentication requirements
     */
    useEffect(() => {
        // Define routes that don't require authentication
        const publicRoutes = [
            '/login',
            '/reset-password',
            '/new-password'
        ];
        
        // Check if current path starts with public routes (for dynamic routes)
        const publicRoutePrefixes = [
            '/public/',
        ];
        
        const isPublicRoute = publicRoutes.includes(location.pathname) ||
                             publicRoutePrefixes.some(prefix => location.pathname.startsWith(prefix));
        
        if (user) {
            // User is authenticated - auth logic handled by other hooks
        } else {
            // Only redirect to login if not on a public route
            if (!isPublicRoute) {
                console.log("Signout useeffect");
                signOut().then(() => {
                    navigate('/login');
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, location.pathname]);

    /**
     * Exchanges Cognito tokens for Strapi JWT and fetches user profile
     * Also updates user's last login timestamp in the database
     * 
     * @returns {Object} Contains Strapi JWT and user data
     */
    const getStrapiUser = async () => {
        let jwtStrapi = "";
        let userStrapi = {};
        try {
            // Exchange Cognito tokens for Strapi JWT
            let request = `${config.apiUrl}/api/auth/cognito/callback?id_token=${idToken}&access_token=${accessToken}`;
            let res = await axios.get(request);
            jwtStrapi = res.data.jwt;
            setJwt(jwtStrapi);
            
            // Fetch user profile with all related data
            userStrapi = await axios.get(`${config.apiUrl}/api/users/me?populate=*`, {
                headers: {
                    Authorization: `Bearer ${jwtStrapi}`,
                },
            });
            
            // Update local state with user profile and limits
            setUserAuth(userStrapi.data);
            setMaxSpace(userStrapi.data.space);
            setMaxFileSize(userStrapi.data.maxFileSize);

            // Update user's last login timestamp
            const currentDateTime = new Date().toISOString();
            await axios.put(
                `${config.apiUrl}/api/users/${userStrapi.data.id}`,
                { lastLogin: currentDateTime },
                {
                    headers: {
                        Authorization: `Bearer ${jwtStrapi}`,
                    },
                }
            );

        } catch {
            console.log("cannot retrieve strapi user");
            // If Strapi authentication fails, sign out and redirect
            signOut().then(res => {
                let path = `/login`;
                navigate(path);
            })
        }
        return {
            jwtStrapi: jwtStrapi,
            userStrapi: userStrapi
        };
    };

    return {
        user,
        userAuth,
        setUserAuth,
        loading,
        setLoading,
        getStrapiUser,
        jwt,
        idToken,
        accessToken,
        service
    };
};